"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AgentForm } from "../agent-form";
import { PbxFlowsSection } from "./pbx-flows-section";
import type { VoiceAgent } from "@/hooks/agents/voice";
import { AiDisclosureSettings } from "@/components/organization/ai-disclosure-settings";
import { useTranslations } from "next-intl"


interface GeneralSettingsProps {
  agent: VoiceAgent | null;
  isCreating: boolean;
  onSave: (agent: VoiceAgent) => void;
  onSavingChange?: (isSaving: boolean) => void;
  onStatusChange: (agentId: string, status: "active" | "disabled") => Promise<void>;
  isUpdatingStatus: boolean;
}

export function GeneralSettings({
  agent,
  isCreating,
  onSave,
  onSavingChange,
  onStatusChange,
  isUpdatingStatus,
}: GeneralSettingsProps) {
  const tUi = useTranslations("Voice.AgentsVoiceSettingsGeneralSettings")

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground"><span key={tUi("general")}>{tUi("general")}</span></h2>
        <p className="text-sm text-muted-foreground mt-1">
          <span key={tUi("basicConfigurationForYourVoiceAgent")}>{tUi("basicConfigurationForYourVoiceAgent")}</span></p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle><span key={tUi("agentDetails")}>{tUi("agentDetails")}</span></CardTitle>
        </CardHeader>
        <CardContent>
          <AgentForm
            key={agent?.id ?? "new"}
            agent={agent}
            onSave={onSave}
            formId="agent-form"
            hideButtons
            onSavingChange={onSavingChange}
            onStatusChange={onStatusChange}
            isUpdatingStatus={isUpdatingStatus}
          />
        </CardContent>
      </Card>

      <AiDisclosureSettings surface="voice" />

      {isCreating && (
        <Alert className="border-blue-200 bg-blue-50">
          <AlertDescription className="text-blue-800">
            <span key={tUi("saveTheAgentToUnlockAdditionalSettingsLike")}>{tUi("saveTheAgentToUnlockAdditionalSettingsLike")}</span></AlertDescription>
        </Alert>
      )}

      {!isCreating && agent && (
        <PbxFlowsSection agentId={agent.id} />
      )}
    </div>
  );
}
